import { useEffect, useRef, useState } from 'react';
import { api, errorText } from '../../lib/api';
import type { Layout } from '../../lib/contentTypes';

// Film preview (issue 40) and music upload (issue 41) of the motion dialog.
//
// The preview is an APPROXIMATION: the frames are drawn by the one renderer (Motion.tsx hands them in),
// the camera move is a canvas transform over the slide's duration, text reveals are approximated (fade,
// rise, wipe, letter by letter), slides crossfade. Mirage Video draws the real film.

/** Tracks uploaded from here, by Mirage Video library id: the browser keeps the file it was given so the
 *  preview can play it. A track uploaded elsewhere has no copy here ("music plays in the final film only"). */
export const musicPreviewUrls = new Map<string, string>();

export const MAX_MUSIC_BYTES = 24 * 1024 * 1024;

export interface MusicTrack {
  id: string;
  label: string | null;
  original_name: string;
  category: string | null;
  duration_sec: number | null;
  credit: string | null;
}

export function MusicUpload({ onUploaded }: { onUploaded: (track: MusicTrack) => void }) {
  const [file, setFile] = useState<File | null>(null);
  const [label, setLabel] = useState('');
  const [credit, setCredit] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState('');

  async function send() {
    if (!file) return;
    setError('');
    setDone('');
    if (file.size > MAX_MUSIC_BYTES) {
      setError('A music file is at most 24 MB.');
      return;
    }
    setBusy(true);
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('label', label.trim());
      form.append('credit', credit.trim());
      const r = await api<{ music: MusicTrack; created: boolean }>('/api/videos/music', { method: 'POST', form });
      musicPreviewUrls.set(r.music.id, URL.createObjectURL(file));
      onUploaded(r.music);
      setDone(r.created ? `“${r.music.label ?? label}” was added to Mirage Video's music library and chosen.` : `“${r.music.label ?? label}” was already in the library: it is chosen.`);
      setFile(null);
      setLabel('');
      setCredit('');
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <details className="cs-music-upload">
      <summary>Upload music</summary>
      <div className="stack">
        <p className="muted small">MP3, M4A/AAC or WAV, at most 24 MB. It goes into Mirage Video's music library; nothing is kept here. Give its credit if the licence asks for one.</p>
        <label className="field">
          <span>Audio file</span>
          <input
            type="file"
            accept="audio/mpeg,audio/mp4,audio/x-m4a,audio/aac,audio/wav,audio/x-wav,.mp3,.m4a,.aac,.wav"
            aria-label="Music file"
            onChange={(e) => {
              const f = e.target.files?.[0] ?? null;
              setFile(f);
              if (f && !label) setLabel(f.name.replace(/\.[^.]+$/, '').slice(0, 200));
            }}
          />
        </label>
        <label className="field">
          <span>Name of the track</span>
          <input value={label} maxLength={200} onChange={(e) => setLabel(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && e.preventDefault()} aria-label="Music name" />
        </label>
        <label className="field">
          <span>Credit / licence (optional)</span>
          <input value={credit} maxLength={500} onChange={(e) => setCredit(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && e.preventDefault()} aria-label="Music credit" />
        </label>
        <div className="row gap">
          <button type="button" className="btn small" disabled={!file || !label.trim() || busy} onClick={send}>
            {busy ? 'Uploading…' : 'Upload the track'}
          </button>
        </div>
        {done && (
          <p className="small" role="status" data-music-status="done">
            {done}
          </p>
        )}
        {error && (
          <p className="msg error" role="alert">
            {error}
          </p>
        )}
      </div>
    </details>
  );
}

// --- the camera ------------------------------------------------------------------------------------

const AMOUNT: Record<string, number> = { SUBTLE: 0.06, NORMAL: 0.12, STRONG: 0.2 };
const clamp01 = (x: number) => Math.min(1, Math.max(0, x));

export interface Camera {
  scale: number;
  /** shift in fractions of the frame's width / height */
  tx: number;
  ty: number;
  /** radians */
  rot: number;
}

/** The camera of one shot at progress p (0..1): zoom in/out about the focus point, pans and rises as a
 *  shift under a slight zoom, an orbit as a small turn. Same words as Mirage Video's vocabulary. */
export function cameraAt(movement: string, intensity: string, p: number): Camera {
  const k = AMOUNT[intensity] ?? AMOUNT.NORMAL;
  const still: Camera = { scale: 1, tx: 0, ty: 0, rot: 0 };
  switch (movement) {
    case 'PUSH_IN':
      return { ...still, scale: 1 + k * p };
    case 'PULL_OUT':
      return { ...still, scale: 1 + k * (1 - p) };
    case 'PAN_LEFT':
      return { ...still, scale: 1 + k, tx: (p - 0.5) * k };
    case 'PAN_RIGHT':
      return { ...still, scale: 1 + k, tx: (0.5 - p) * k };
    case 'RISE':
      return { ...still, scale: 1 + k, ty: (p - 0.5) * k };
    case 'LOWER':
      return { ...still, scale: 1 + k, ty: (0.5 - p) * k };
    case 'ORBIT_LEFT':
      return { ...still, scale: 1 + k * 0.6, rot: (0.5 - p) * k * 0.5 };
    case 'ORBIT_RIGHT':
      return { ...still, scale: 1 + k * 0.6, rot: (p - 0.5) * k * 0.5 };
    default:
      return still;
  }
}

const ease = (p: number) => (p < 0.5 ? 2 * p * p : 1 - 2 * (1 - p) * (1 - p));
const REVEAL_AT = 0.3; // seconds into the slide
const REVEAL_FOR = 0.8;

export interface PreviewShot {
  movement: string;
  intensity: string;
  seconds: number;
  fx: number;
  fy: number;
  texts: { layerId: string; reveal: string }[];
}

interface Built {
  bare: HTMLCanvasElement;
  full: HTMLCanvasElement;
}

/** How a reveal shows a text's box at progress r (0..1): the part of the full frame drawn over the bare one. */
function drawReveal(ctx: CanvasRenderingContext2D, full: HTMLCanvasElement, box: { x: number; y: number; w: number; h: number }, reveal: string, r: number) {
  if (r <= 0) return;
  const { x, y, w, h } = box;
  ctx.save();
  ctx.beginPath();
  if (reveal === 'WIPE') ctx.rect(x, y, w * r, h);
  else if (reveal === 'LETTERS') ctx.rect(x, y, w * (Math.ceil(r * 24) / 24), h);
  else if (reveal === 'MASK') ctx.rect(x, y, w, h * r);
  else ctx.rect(x, y, w, h);
  ctx.clip();
  if (reveal === 'FADE' || reveal === 'TRACKING') ctx.globalAlpha = r;
  let dy = 0;
  if (reveal === 'RISE') {
    ctx.globalAlpha = r;
    dy = (1 - r) * h * 0.4;
  }
  // The box of the full frame over the bare frame: the bare background shows through the fade.
  ctx.drawImage(full, x, y, w, h, x, y + dy, w, h);
  ctx.restore();
}

export function MotionPreview({
  layout,
  size,
  frames,
  shots,
  transition,
  pace,
  musicUrl,
  onError,
}: {
  layout: Layout;
  /** the preview canvas, in pixels (the film's aspect) */
  size: [number, number];
  /** frame of slide i drawn by the one renderer, without the layers in `moving` */
  frames: (index: number, moving: Set<string>, size: [number, number]) => HTMLCanvasElement;
  shots: PreviewShot[];
  transition: string;
  pace: string;
  musicUrl: string | null;
  onError: (message: string) => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  const built = useRef<Built[]>([]);
  const timeRef = useRef(0);
  const [time, setTime] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [ready, setReady] = useState(false);

  const total = shots.reduce((n, s) => n + s.seconds, 0);
  const shotsRef = useRef(shots);
  shotsRef.current = shots;

  // Draw every slide twice: bare (the moving texts left out, as the film's picture) and full.
  const movingKey = shots.map((s) => s.texts.filter((t) => t.reveal !== 'NONE').map((t) => t.layerId).join(',')).join('|');
  useEffect(() => {
    try {
      built.current = shotsRef.current.map((s, i) => ({
        bare: frames(i, new Set(s.texts.filter((t) => t.reveal !== 'NONE').map((t) => t.layerId)), size),
        full: frames(i, new Set(), size),
      }));
      setReady(true);
    } catch (e) {
      onError(errorText(e));
    }
    // frames are redrawn when the slides, the moving texts or the canvas change, not on every choice
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shots.length, movingKey, size[0], size[1]]);

  function paint(t: number) {
    const c = canvasRef.current;
    const ctx = c?.getContext('2d');
    const list = shotsRef.current;
    if (!c || !ctx || !built.current.length || built.current.length !== list.length) return;
    const [W, H] = size;
    const s = Math.min(W / layout.width, H / layout.height);
    const ox = (W - layout.width * s) / 2;
    const oy = (H - layout.height * s) / 2;
    const box = (index: number, id: string) => {
      const l = layout.artboards[index]?.layers.find((x) => x.id === id);
      if (!l) return null;
      const pad = 6;
      const x = Math.max(0, ox + l.x * s - pad);
      const y = Math.max(0, oy + l.y * s - pad);
      return { x, y, w: Math.min(W - x, l.w * s + 2 * pad), h: Math.min(H - y, l.h * s + 2 * pad) };
    };
    const drawSlide = (i: number, local: number, alpha: number) => {
      const sh = list[i];
      const b = built.current[i];
      const p0 = clamp01(local / sh.seconds);
      const cam = cameraAt(sh.movement, sh.intensity, pace === 'CONSTANT' ? p0 : ease(p0));
      ctx.save();
      ctx.globalAlpha = alpha;
      ctx.translate(sh.fx * W + cam.tx * W, sh.fy * H + cam.ty * H);
      ctx.rotate(cam.rot);
      ctx.scale(cam.scale, cam.scale);
      ctx.translate(-sh.fx * W, -sh.fy * H);
      ctx.drawImage(b.bare, 0, 0, W, H);
      for (const tx of sh.texts) {
        if (tx.reveal === 'NONE') continue;
        const bx = box(i, tx.layerId);
        if (bx) drawReveal(ctx, b.full, bx, tx.reveal, clamp01((local - REVEAL_AT) / REVEAL_FOR));
      }
      ctx.restore();
    };
    const t0 = Math.min(Math.max(0, t), Math.max(0, total - 1e-6));
    let start = 0;
    let i = 0;
    while (i < list.length - 1 && t0 >= start + list[i].seconds) {
      start += list[i].seconds;
      i++;
    }
    const local = t0 - start;
    ctx.clearRect(0, 0, W, H);
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, W, H);
    const tr = transition === 'CUT' || i === list.length - 1 ? 0 : Math.min(0.5, list[i].seconds / 2, list[i + 1].seconds / 2);
    const a = tr ? clamp01((local - (list[i].seconds - tr)) / tr) : 0;
    if (transition === 'FADE_BLACK' && tr) {
      if (a < 0.5) drawSlide(i, local, 1 - 2 * a);
      else drawSlide(i + 1, 0, 2 * (a - 0.5));
    } else {
      drawSlide(i, local, 1);
      if (a > 0) drawSlide(i + 1, 0, a);
    }
  }

  // Draw when the time or the choices change; advance the time while playing.
  useEffect(() => {
    if (ready) paint(timeRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, shots, transition, pace]);

  useEffect(() => {
    if (!playing) return;
    let raf = 0;
    let last = performance.now();
    const step = (now: number) => {
      let t = timeRef.current + (now - last) / 1000;
      last = now;
      if (t >= total) {
        t = total;
        setPlaying(false);
      }
      timeRef.current = t;
      paint(t);
      setTime(t);
      if (t < total) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playing, total]);

  useEffect(() => {
    const a = audioRef.current;
    if (!a || !musicUrl) return;
    if (playing) {
      a.currentTime = Math.min(timeRef.current, a.duration || timeRef.current);
      void a.play().catch(() => undefined);
    } else a.pause();
  }, [playing, musicUrl]);

  function seek(t: number) {
    timeRef.current = t;
    setTime(t);
    paint(t);
    const a = audioRef.current;
    if (a && musicUrl) a.currentTime = Math.min(t, a.duration || t);
  }

  return (
    <section className="cs-motion-preview" aria-label="Film preview">
      <canvas ref={canvasRef} width={size[0]} height={size[1]} aria-label="Film preview picture" data-time={time.toFixed(2)} />
      <div className="row gap wrap cs-motion-preview-bar">
        <button
          type="button"
          className="btn small"
          disabled={!ready}
          onClick={() => {
            if (!playing && timeRef.current >= total) seek(0);
            setPlaying(!playing);
          }}
        >
          {playing ? 'Pause' : 'Play'}
        </button>
        <input
          type="range"
          min={0}
          max={total}
          step={0.1}
          value={Math.min(time, total)}
          aria-label="Preview time"
          onChange={(e) => {
            setPlaying(false);
            seek(Number(e.target.value));
          }}
        />
        <span className="small" data-preview-clock>
          {time.toFixed(1)} s / {total.toFixed(1)} s
        </span>
      </div>
      <p className="muted small">
        Approximate: the film is drawn by Mirage Video. {musicUrl ? 'The track you uploaded plays here.' : 'Music plays in the final film only.'}
      </p>
      {musicUrl && <audio ref={audioRef} src={musicUrl} preload="auto" />}
    </section>
  );
}
